import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AppStateContext from "contexts/AppStateContext";
import { useToast } from "contexts/ToastContext";
import { useContext, useState } from "react";
import {
  CARRIERS,
  CARRIER_LABELS,
  Carrier,
  ShipmentType,
} from "types/ShippingTypes";
import {
  apiCancelShipment,
  apiGetLabel,
  apiGetPackingSlip,
  apiMarkCollected,
  apiMarkSent,
  apiRebookShipment,
  apiReprintLabel,
  apiUpdateOwnDeliveryCost,
} from "util/network/Shipping";
import ParcelsForm, {
  ParcelDraft,
  emptyParcel,
  parcelsToInput,
} from "./ParcelsForm";
import {
  AppLink,
  ShipmentStateChip,
  downloadLabel,
  errorText,
  formatDateTime,
  formatKg,
  formatKr,
  printPackingSlip,
  shipmentKindText,
  useIsManager,
} from "./common";

type Props = {
  shipments: ShipmentType[];
  /** A changed row (cancel, collected, cost, ...) is patched in place. */
  onChanged: (shipment: ShipmentType) => void;
  /** A rebook cancels the old row and returns the new one. */
  onRebooked?: (old: ShipmentType, created: ShipmentType) => void;
  showOrder?: boolean;
  /** The order's band and estimate, for the rebook dialog's warnings. */
  paidToKg?: number | null;
  estimateGrams?: number | null;
};

const shipmentDate = (shipment: ShipmentType) =>
  shipment.cancelledAt ??
  shipment.deliveredAt ??
  shipment.sentAt ??
  shipment.collectedAt ??
  shipment.handedAt ??
  shipment.readyAt ??
  shipment.bookedAt;

const ShipmentsTable: React.FC<Props> = ({
  shipments,
  onChanged,
  onRebooked,
  showOrder,
  paidToKg = null,
  estimateGrams = null,
}) => {
  const { token } = useContext(AppStateContext);
  const { addToast } = useToast();
  const isManager = useIsManager();

  const [busyId, setBusyId] = useState<number | null>(null);
  const [cancelTarget, setCancelTarget] = useState<ShipmentType | null>(null);
  const [rebookTarget, setRebookTarget] = useState<ShipmentType | null>(null);
  const [rebookParcels, setRebookParcels] = useState<ParcelDraft[]>([
    emptyParcel(),
  ]);
  const [rebookCarrier, setRebookCarrier] = useState<Carrier>("gls");
  const [costTarget, setCostTarget] = useState<ShipmentType | null>(null);
  const [cost, setCost] = useState("");

  const run = <T,>(
    shipment: ShipmentType,
    action: () => Promise<T>,
    onDone: (result: T) => void,
    success?: string
  ) => {
    setBusyId(shipment.id);
    action()
      .then((result) => {
        onDone(result);
        if (success) addToast({ type: "success", message: success });
      })
      .catch((error) => addToast({ type: "error", message: errorText(error) }))
      .finally(() => setBusyId(null));
  };

  const download = (shipment: ShipmentType) =>
    run(
      shipment,
      () => apiGetLabel(token, shipment.id),
      (label) =>
        downloadLabel(
          label.base64,
          label.fileFormat,
          `label-${shipment.orderNo ?? "unmatched"}-${shipment.id}`
        )
    );

  const slip = (shipment: ShipmentType) =>
    run(
      shipment,
      () => apiGetPackingSlip(token, shipment.id),
      (data) => {
        if (!printPackingSlip(data)) {
          addToast({
            type: "warning",
            message: "The browser blocked the print window; allow pop-ups.",
          });
        }
      }
    );

  const openRebook = (shipment: ShipmentType) => {
    setRebookParcels([emptyParcel()]);
    setRebookCarrier((shipment.carrier as Carrier) ?? "gls");
    setRebookTarget(shipment);
  };

  const confirmRebook = () => {
    const target = rebookTarget;
    const parcels = parcelsToInput(rebookParcels);
    if (!target || !parcels) return;
    run(
      target,
      () =>
        apiRebookShipment(token, target.id, {
          parcels,
          carrier: rebookCarrier,
        }),
      (created) => {
        setRebookTarget(null);
        onRebooked?.(target, created);
      },
      "Label rebooked"
    );
  };

  const confirmCancel = () => {
    const target = cancelTarget;
    if (!target) return;
    run(
      target,
      () => apiCancelShipment(token, target.id),
      (updated) => {
        setCancelTarget(null);
        onChanged(updated);
      },
      target.kind === "carrier" ? "Label cancelled" : "Shipment cancelled"
    );
  };

  const confirmCost = () => {
    const target = costTarget;
    const value = Number(cost.replace(",", "."));
    if (!target || cost.trim() === "" || !Number.isFinite(value) || value < 0)
      return;
    run(
      target,
      () => apiUpdateOwnDeliveryCost(token, target.id, value),
      (updated) => {
        setCostTarget(null);
        onChanged(updated);
      },
      "Cost saved"
    );
  };

  if (shipments.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No shipments yet.
      </Typography>
    );
  }

  const rebookReady = parcelsToInput(rebookParcels) !== null;

  return (
    <>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {showOrder && <TableCell>Order</TableCell>}
              <TableCell>Shipment</TableCell>
              <TableCell>State</TableCell>
              <TableCell>Tracking</TableCell>
              <TableCell>Parcels</TableCell>
              <TableCell>Lines</TableCell>
              <TableCell>Cost (ex VAT)</TableCell>
              <TableCell>Date</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {shipments.map((shipment) => {
              const busy = busyId === shipment.id;
              const carrier = shipment.kind === "carrier";
              const cost =
                shipment.kind === "own_delivery"
                  ? shipment.ownDeliveryCost
                  : shipment.priceExVat;
              return (
                <TableRow
                  key={shipment.id}
                  sx={{
                    opacity: shipment.state === "cancelled" ? 0.55 : 1,
                  }}
                >
                  {showOrder && (
                    <TableCell>
                      {shipment.orderId ? (
                        <AppLink to={`/shipment/packing/${shipment.orderId}`}>
                          {shipment.orderNo}
                        </AppLink>
                      ) : (
                        "Unmatched"
                      )}
                    </TableCell>
                  )}
                  <TableCell>
                    {shipmentKindText(shipment)}
                    {shipment.source === "shipmondo_ui" && (
                      <Typography variant="caption" display="block">
                        Shipmondo UI
                        {shipment.shipmondoSourceType === "repurchase"
                          ? " (repurchase)"
                          : ""}
                      </Typography>
                    )}
                    {shipment.replacesId && (
                      <Typography variant="caption" display="block">
                        Rebook of #{shipment.replacesId}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <ShipmentStateChip state={shipment.state} />
                  </TableCell>
                  <TableCell>
                    {shipment.trackingUrl ? (
                      <Link
                        href={shipment.trackingUrl}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {shipment.trackingNo}
                      </Link>
                    ) : (
                      shipment.trackingNo
                    )}
                  </TableCell>
                  <TableCell>
                    {shipment.parcels
                      .map((parcel) =>
                        parcel.quantity > 1
                          ? `${parcel.quantity} × ${formatKg(
                              parcel.weightGrams
                            )}`
                          : formatKg(parcel.weightGrams)
                      )
                      .join(", ")}
                  </TableCell>
                  <TableCell>
                    {shipment.lines.length === 0 ? (
                      <Typography variant="caption" color="error">
                        Not assigned
                      </Typography>
                    ) : (
                      shipment.lines.map((line) => (
                        <Typography
                          key={line.orderItemId}
                          variant="caption"
                          display="block"
                        >
                          {line.quantity} × {line.name}
                        </Typography>
                      ))
                    )}
                  </TableCell>
                  <TableCell>
                    {formatKr(cost)}
                    {!!shipment.extraChargesExVat && (
                      <Typography
                        variant="caption"
                        display="block"
                        color="error"
                      >
                        + {formatKr(shipment.extraChargesExVat)} extra
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {formatDateTime(shipmentDate(shipment))}
                  </TableCell>
                  <TableCell align="right">
                    <Box
                      sx={{
                        display: "flex",
                        gap: 0.5,
                        justifyContent: "flex-end",
                        flexWrap: "wrap",
                      }}
                    >
                      {busy && <CircularProgress size={20} />}
                      {carrier &&
                        shipment.hasLabel &&
                        shipment.state !== "cancelled" && (
                          <>
                            <Button
                              size="small"
                              disabled={busy}
                              onClick={() =>
                                run(
                                  shipment,
                                  () => apiReprintLabel(token, shipment.id),
                                  () => {},
                                  "Sent to the label printer"
                                )
                              }
                            >
                              Reprint
                            </Button>
                            <Button
                              size="small"
                              disabled={busy}
                              onClick={() => download(shipment)}
                            >
                              Download label
                            </Button>
                          </>
                        )}
                      {shipment.orderId &&
                        shipment.lines.length > 0 &&
                        shipment.state !== "cancelled" && (
                          <Button
                            size="small"
                            disabled={busy}
                            onClick={() => slip(shipment)}
                          >
                            Packing slip
                          </Button>
                        )}
                      {carrier && shipment.state === "booked" && onRebooked && (
                        <Button
                          size="small"
                          disabled={busy || shipment.lines.length === 0}
                          onClick={() => openRebook(shipment)}
                        >
                          Rebook
                        </Button>
                      )}
                      {shipment.canMarkSent && isManager && (
                        <Button
                          size="small"
                          disabled={busy}
                          onClick={() =>
                            run(
                              shipment,
                              () => apiMarkSent(token, shipment.id),
                              onChanged,
                              "Marked as sent"
                            )
                          }
                        >
                          Mark as sent
                        </Button>
                      )}
                      {shipment.kind === "pickup" &&
                        shipment.state === "ready" && (
                          <Button
                            size="small"
                            variant="contained"
                            disabled={busy}
                            onClick={() =>
                              run(
                                shipment,
                                () => apiMarkCollected(token, shipment.id),
                                onChanged,
                                "Marked as collected"
                              )
                            }
                          >
                            Collected
                          </Button>
                        )}
                      {shipment.kind === "own_delivery" &&
                        isManager &&
                        shipment.state !== "cancelled" && (
                          <Button
                            size="small"
                            disabled={busy}
                            onClick={() => {
                              setCost(String(shipment.ownDeliveryCost ?? 0));
                              setCostTarget(shipment);
                            }}
                          >
                            Edit cost
                          </Button>
                        )}
                      {shipment.canCancel && (
                        <Button
                          size="small"
                          color="error"
                          disabled={busy}
                          onClick={() => setCancelTarget(shipment)}
                        >
                          Cancel
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={!!cancelTarget} onClose={() => setCancelTarget(null)}>
        <DialogTitle>Cancel shipment</DialogTitle>
        <DialogContent>
          {cancelTarget?.kind === "carrier"
            ? "The label is cancelled at Shipmondo and its lines go back to the packing queue."
            : "The shipment is undone and its lines go back to the packing queue."}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelTarget(null)}>Keep</Button>
          <Button
            color="error"
            variant="contained"
            disabled={busyId !== null}
            onClick={confirmCancel}
          >
            Cancel shipment
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!rebookTarget}
        onClose={() => setRebookTarget(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Rebook label</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            The current label is cancelled and a new one is booked for the same
            lines.
          </Typography>
          <FormControl size="small" sx={{ mb: 2, minWidth: 200 }}>
            <InputLabel>Carrier</InputLabel>
            <Select
              label="Carrier"
              value={rebookCarrier}
              onChange={(event) =>
                setRebookCarrier(event.target.value as Carrier)
              }
            >
              {CARRIERS.map((value) => (
                <MenuItem key={value} value={value}>
                  {CARRIER_LABELS[value]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <ParcelsForm
            parcels={rebookParcels}
            onChange={setRebookParcels}
            estimateGrams={estimateGrams}
            paidToKg={paidToKg}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRebookTarget(null)}>Close</Button>
          <Button
            variant="contained"
            disabled={!rebookReady || busyId !== null}
            onClick={confirmRebook}
          >
            Rebook
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!costTarget} onClose={() => setCostTarget(null)}>
        <DialogTitle>Own-delivery cost</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Cost ex VAT (kr)"
            value={cost}
            onChange={(event) => setCost(event.target.value)}
            inputProps={{ inputMode: "decimal" }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCostTarget(null)}>Close</Button>
          <Button
            variant="contained"
            disabled={busyId !== null}
            onClick={confirmCost}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ShipmentsTable;
